import IconButton from 'components/@extended/IconButton';
import { Tooltip } from 'components/ui/tooltip';
import { CopiedStatus, CopyGlyph } from 'components/util/CopiedStatus';
import { useEffect, useRef, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

/**
 * Hides the icon until the pointer rests on the nearest `group/copy` ancestor, so a table column does not
 * show a copy button on every row. Keyboard focus reveals it too. Devices without a hovering fine pointer
 * (touch screens, pens without hover) always show it.
 */
export const REVEAL_ON_COPY_GROUP_HOVER_CLASS =
    'transition-opacity [@media(hover:hover)_and_(pointer:fine)]:opacity-0 [@media(hover:hover)_and_(pointer:fine)]:group-hover/copy:opacity-100 [@media(hover:hover)_and_(pointer:fine)]:focus-within:opacity-100';

export default function CopyToClipboardIcon({ text, className }: { text: string; className?: string }) {
    const label = t('copy-to-clipboard');
    const [copied, setCopied] = useState(false);
    const timeoutRef = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    return (
        // The tooltip trigger is the span: `CopyToClipboard` clones its child to add the click handler,
        // so it has to sit directly on the button.
        <Tooltip content={label} closeOnClick={false}>
            <span className={cn('ml-1 inline-flex', className)}>
                <CopyToClipboard
                    text={text}
                    onCopy={() => {
                        setCopied(true);
                        if (timeoutRef.current) clearTimeout(timeoutRef.current);
                        timeoutRef.current = window.setTimeout(() => setCopied(false), 1500);
                    }}
                >
                    <IconButton aria-label={label} className="text-fg-secondary hover:text-fg size-6">
                        <CopyGlyph copied={copied} />
                    </IconButton>
                </CopyToClipboard>
                <CopiedStatus copied={copied} />
            </span>
        </Tooltip>
    );
}
